/**
 * SkipToContentLink — WCAG 2.4.1 Bypass Blocks (Level A).
 *
 * Found by the 2026-08-21/22 E2E sweep: neither authenticated shell had one,
 * so a keyboard user hit ~18 sidebar tab stops on EVERY page before reaching
 * the content. `grep skip-link` returned zero hits across the whole frontend.
 *
 * Shared by both shells (ScreenShell and AdminLayout) on purpose — a skip link
 * duplicated per shell is a skip link that drifts. It must be rendered as the
 * FIRST focusable element inside the shell's outermost element, and the
 * matching content container must carry `id="main"` and `tabIndex={-1}` so it
 * can accept programmatic focus.
 *
 * Why the click is intercepted rather than left to the browser: a bare
 * `href="#main"` would push `#main` onto the URL, and this app's routing reads
 * the location (TanStack Router search/hash). Calling `.focus()` directly moves
 * both the focus ring and the AT cursor — which is the actual requirement —
 * without touching the URL. The `href` stays so the element is a real link with
 * link semantics and keyboard activation.
 *
 * Styling lives in design-system.css (`.skip-link`): off-screen until focused.
 */

export function SkipToContentLink({ targetId = 'main' }: { targetId?: string }) {
  return (
    <a
      className="skip-link"
      href={`#${targetId}`}
      data-testid="skip-to-content"
      onClick={(e) => {
        e.preventDefault();
        const el = document.getElementById(targetId);
        if (!el) return;
        el.focus();
        // Absent in jsdom and some older embedded webviews — the focus above
        // is the part that matters, the scroll is a nicety.
        if (typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'start' });
        }
      }}
    >
      Skip to content
    </a>
  );
}
